import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { setFiles, processAfterSubmitAction, setPristine } from '#action/shipment/form';
import { reduxFormResolver } from '#helper/rhf-resolver';
import { useStepSubmit } from '#component/shipment/form/use-step-submit';
import validate from './form/validate';
import Form from './form';
import { isUndefinedOrNull } from '#helper';

const handleFormSubmit = dispatch => async values => {
  const { files = [], filesToDelete = [] } = values;
  const filesToPost = files.filter(f => !isUndefinedOrNull(f.file));
  dispatch(setFiles(filesToPost, filesToDelete));
  await dispatch(processAfterSubmitAction());
};

const DocumentsForm = props => {
  const { initialValues, onSubmit, editable, registerStepSubmit, form } = props;
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: reduxFormResolver((values, context) => validate(values, { ...props, ...context })),
    defaultValues: initialValues || { files: [], filesToDelete: [] },
    mode: 'onChange'
  });

  const { reset, formState: { isDirty } } = methods;
  const prevInitialRef = React.useRef(null);

  useEffect(() => {
    if (!initialValues) return;
    const serialized = JSON.stringify(initialValues);
    if (prevInitialRef.current === serialized) return;
    prevInitialRef.current = serialized;
    if (!isDirty) {
      reset(initialValues);
    }
  }, [initialValues, reset, isDirty]);

  useEffect(() => {
    if (form) {
      dispatch(setPristine(form, !isDirty));
    }
  }, [dispatch, form, isDirty]);

  const onFormSubmit = async values => {
    if (editable) {
      await onSubmit(values);
    } else {
      dispatch(processAfterSubmitAction());
    }
  };

  useStepSubmit(registerStepSubmit, methods, onFormSubmit);

  return (
    <FormProvider {...methods}>
      <Form {...props} onFormSubmit={onFormSubmit} />
    </FormProvider>
  );
};

const mapStateToProps = (state, props) => {
  const { shipment: { files = [] }, shipment, shipmentForm } = state;
  const { dispatch, editable } = props;

  const currentFiles = (shipmentForm.files?.filesToPost?.length ?
    shipmentForm.files.filesToPost : files) ?? [];

  const initialValues = {
    files: currentFiles.map(file => {
      const extension = file.name?.match(/[^.]+$/i)?.[0] ?? '';

      return ({
        id: file.id,
        name: file.name?.slice(0, file.name.length - extension.length - 1) ?? file.name,
        extension,
        description: file.description || ''
      });
    }),
    filesToDelete: []
  };

  return {
    initialValues,
    shipment,
    editable,
    onSubmit: handleFormSubmit(dispatch)
  };
};

export default connect(mapStateToProps)(DocumentsForm);
